// ============================================================
// لایه‌ی سوم — مدل زبانی (اختیاری و پیش‌فرض خاموش)
//
// این برنامه یک SPA است؛ هیچ سمت‌سروری از خودش ندارد. پس کلید مدل
// را *نمی‌شود* اینجا گذاشت: هر چیزی که با REACT_APP_ شروع شود داخل
// باندل جاوااسکریپت می‌نشیند و برای هر بازدیدکننده قابل خواندن است.
// همان تله‌ای که یک بار کلید را روی گیت‌هاب عمومی کرد.
//
// راه درست: یک مسیر روی سرور خودتان که کلید را نگه می‌دارد و
// {message, history, context} می‌گیرد و {reply} برمی‌گرداند. آدرس
// همان مسیر در REACT_APP_ASSISTANT_LLM_URL گذاشته می‌شود.
//
// اگر این متغیر خالی باشد، دستیار کامل کار می‌کند — فقط لایه‌ی سوم
// ندارد و به جایش پایگاه دانش محلی جواب می‌دهد.
// ============================================================

const TIMEOUT_MS = 25000;

export const llmEndpoint = (): string =>
    String(process.env.REACT_APP_ASSISTANT_LLM_URL || "").trim();

export const isLlmEnabled = (): boolean => llmEndpoint().length > 0;

/**
 * حالت تازه‌ی OpenAI: دستورها و داده‌های سامانه همچنان محلی می‌مانند،
 * اما گفت‌وگوی آزاد پیش از پایگاه دانش به مدل وصل می‌شود.
 * پیش‌فرض خالی است تا نسخه‌ی فعلی بدون تغییر رفتار کند.
 */
export const isOpenAiMode = (): boolean =>
    String(process.env.REACT_APP_ASSISTANT_MODE || "").trim().toLowerCase() === "openai";

/**
 * آیا اجازه داریم خلاصه‌ی رکوردهای واقعی را هم به مدل بفرستیم؟
 * پیش‌فرض: نه. داده‌ی سامانه بدون تصمیم صریح از سازمان بیرون نمی‌رود.
 */
export const canSendResults = (): boolean =>
    String(process.env.REACT_APP_ASSISTANT_LLM_SEND_RESULTS || "") === "1";

export interface LlmTurn {
    role: "user" | "assistant";
    content: string;
}

export interface LlmRequest {
    message: string;
    /** ۱۲ پیام آخر — هم گفت‌وگو پیوسته می‌ماند هم توکن سقف دارد */
    history: LlmTurn[];
    context: {
        roleLabel: string;
        allowedPages: string[];
        allowedDatasets: string[];
        /** فقط اگر canSendResults() روشن باشد پر می‌شود */
        lastResultSummary?: string;
    };
}

/**
 * null یعنی «لایه‌ی سوم جواب نداد» — نه خطا برای کاربر.
 * کاربر هیچ‌وقت پیام خطای مدل را نمی‌بیند؛ به جایش پاسخ محلی می‌گیرد.
 */
/** آدرس مسیر صدا، از همان ریشه‌ی مسیر گفتگو ساخته می‌شود. */
export const ttsEndpoint = (): string => {
    const chat = llmEndpoint();
    if (!chat) return "";
    return chat.replace(/\/chat$/, "/speak");
};

export const isCloudTtsEnabled = (): boolean =>
    isOpenAiMode() && ttsEndpoint().length > 0;

/**
 * آدرس پخش جریانی صدا.
 *
 * چرا GET و نه fetch: با fetch باید کل mp3 بیاید، بعد Blob بسازیم،
 * بعد پخش کنیم — یعنی ۱.۵ تا ۲.۸ ثانیه سکوت. وقتی همین آدرس را به
 * <audio> بدهیم، مرورگر جریانی می‌گیرد و از اولین تکه‌ها پخش را
 * شروع می‌کند.
 *
 * متن در آدرس می‌رود، پس سقف ۶۰۰ نویسه‌ای (که سرور هم اعمالش می‌کند)
 * اینجا هم لازم است تا آدرس بیش از حد بلند نشود.
 */
export const speechUrl = (text: string): string => {
    const base = ttsEndpoint();
    if (!base) return "";
    return base + "?text=" + encodeURIComponent(String(text).slice(0, 600));
};

/**
 * متن را به mp3 تبدیل می‌کند. کلید سمت سرور می‌ماند؛ مرورگر فقط متن
 * می‌فرستد و صدا می‌گیرد. null یعنی نشد — صدای مرورگر جایش را بگیرد.
 */
export const fetchSpeech = async (text: string): Promise<Blob | null> => {
    const url = ttsEndpoint();
    if (!url) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ text: String(text).slice(0, 600) }),
            signal: controller.signal,
        });

        if (!response.ok) return null;

        const blob = await response.blob();
        return blob.size > 0 ? blob : null;
    } catch {
        return null;
    } finally {
        clearTimeout(timer);
    }
};

export const askLlm = async (
    request: LlmRequest,
    mode: "chat" | "phrase" = "chat"
): Promise<string | null> => {
    const url = llmEndpoint();
    if (!url) return null;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                mode,
                // حالت جمله‌نویسی، خلاصه‌ی نتیجه را هم داخل پیام دارد و
                // به جای ۵۰۰ نویسه چند هزار نویسه لازم دارد.
                message: String(request.message).slice(0, mode === "phrase" ? 4000 : 500),
                history: mode === "phrase" ? [] : request.history.slice(-12),
                context: request.context,
            }),
            signal: controller.signal,
        });

        if (!response.ok) return null;

        const data = await response.json();
        const reply = String(data?.reply || "").trim();
        return reply || null;
    } catch {
        // انقضای مهلت، قطعی شبکه، پاسخ نامعتبر — همه یک معنا دارند:
        // برگرد به پایگاه دانش محلی.
        return null;
    } finally {
        clearTimeout(timer);
    }
};
